import { useState, useEffect } from 'react';
import { Loader2, Heart, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { apiCall, formatCents } from '@/lib/api';

const PRESETS = [2500, 5000, 10000, 25000, 50000, 100000];

export default function PublicDonate() {
  const [amountCents, setAmountCents] = useState<number>(5000);
  const [custom, setCustom] = useState('');
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [message, setMessage] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [orgName, setOrgName] = useState<string>('our club');

  useEffect(() => {
    apiCall<{ org: { name: string } | null }>('/v1/donations/status')
      .then((r) => { if (r.org?.name) setOrgName(r.org.name); })
      .catch(() => { /* leave default */ });
  }, []);

  const finalCents = custom ? Math.round(parseFloat(custom) * 100) || 0 : amountCents;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (finalCents < 100) { setErr('Minimum donation is $1.00'); return; }
    setSubmitting(true);
    setErr(null);
    try {
      const r = await apiCall<{ url: string }>('/v1/donations/checkout', {
        method: 'POST',
        body: {
          amount_cents: finalCents,
          donor_name: donorName.trim() || null,
          donor_email: donorEmail.trim() || null,
          message: message.trim() || null,
          anonymous,
        },
      });
      window.location.href = r.url;
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Donation failed');
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-4">
        <header className="text-center space-y-2">
          <Heart className="h-8 w-8 mx-auto text-primary" />
          <h1 className="text-2xl font-bold">Support {orgName}</h1>
          <p className="text-sm text-muted-foreground">
            Your donation funds coaching, mat repairs, and scholarships for athletes who can't afford full membership.
          </p>
        </header>

        <form onSubmit={submit}>
          <Card>
            <CardContent className="py-5 space-y-4">
              <div className="grid grid-cols-3 gap-2">
                {PRESETS.map((c) => (
                  <button
                    type="button"
                    key={c}
                    onClick={() => { setAmountCents(c); setCustom(''); }}
                    className={
                      'py-3 rounded border text-center font-medium tabular-nums transition-colors ' +
                      (!custom && amountCents === c ? 'border-primary bg-primary/10 text-primary' : 'border-input hover:border-primary')
                    }
                  >{formatCents(c)}</button>
                ))}
              </div>
              <label className="block text-sm">
                <span className="block text-xs text-muted-foreground mb-1">Other amount ($)</span>
                <input
                  type="number" min="1" step="0.01"
                  value={custom} onChange={(e) => setCustom(e.target.value)}
                  placeholder="e.g. 75"
                  className="w-full border rounded px-3 py-2 text-sm"
                />
              </label>

              <div className="pt-2 border-t space-y-3">
                <label className="block text-sm">
                  <span className="block text-xs text-muted-foreground mb-1">Your name (optional)</span>
                  <input value={donorName} onChange={(e) => setDonorName(e.target.value)} className="w-full border rounded px-3 py-2 text-sm" />
                </label>
                <label className="block text-sm">
                  <span className="block text-xs text-muted-foreground mb-1">Email (for receipt)</span>
                  <input type="email" value={donorEmail} onChange={(e) => setDonorEmail(e.target.value)} className="w-full border rounded px-3 py-2 text-sm" />
                </label>
                <label className="block text-sm">
                  <span className="block text-xs text-muted-foreground mb-1">Message (optional)</span>
                  <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2} className="w-full border rounded px-3 py-2 text-sm" />
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} />
                  <span>Make this donation anonymous</span>
                </label>
              </div>

              {err && <p className="text-sm text-destructive flex items-center gap-1"><AlertCircle className="h-3.5 w-3.5" /> {err}</p>}

              <Button type="submit" className="w-full" disabled={submitting || finalCents < 100}>
                {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Donate {formatCents(finalCents)}
              </Button>
              <p className="text-xs text-muted-foreground text-center">Card details entered securely on Stripe.</p>
            </CardContent>
          </Card>
        </form>
      </div>
    </div>
  );
}
